import { inject, Injectable } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Store } from '@ngxs/store';
import { map, Observable, take } from 'rxjs';
import { JwtHelper } from '@bo/ng-auth';
import { AuthState } from '../store/auth/auth.state';

/**
 * Custom unauthenticated guard that prevents infinite loops by using take(1).
 * This guard allows access only to unauthenticated users.
 * If the user is authenticated, they are redirected to /dashboard.
 */
@Injectable({
  providedIn: 'root',
})
export class CustomUnAuthenticatedGuard {
  private readonly store = inject(Store);
  private readonly router = inject(Router);

  private readonly jwtHelper = new JwtHelper();

  canActivate(): Observable<boolean | UrlTree> {
    return this.store.select(AuthState.token).pipe(
      take(1), // Complete the observable after first emission to prevent infinite loops
      map((token) => {
        // Check if token exists and is valid
        const isAuthenticated = token != null && this.jwtHelper.isTokenValid(token);

        if (isAuthenticated) {
          // User is authenticated, redirect to dashboard
          return this.router.createUrlTree(['/dashboard']);
        }
        // User is not authenticated, allow access
        return true;
      }),
    );
  }
}

/**
 * Functional guard for use in route configuration
 */
export const canActivateUnAuthenticated: CanActivateFn = (): Observable<boolean | UrlTree> => {
  return inject(CustomUnAuthenticatedGuard).canActivate();
};
